import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StateEntityGroupsService } from '../../state-entity-groups.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-state-group-delete',
    templateUrl: './state-group-delete.component.html',
    styleUrls: ['./state-group-delete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StateGroupDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Delete Group Details";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Data properties
  rowData: any;
  gridData: any[] = [];
  filterParams: any = {};
  baseUrl: string = '';

  // Group data
  allSelectedGroups: any[] = [];

  // Global services
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  globalService: any;

  constructor(
    private stateEntityService: StateEntityGroupsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  private initCalled = false;
  private tryInit() {
    const isDataReady = (this.gridData && this.gridData.length > 0 && this.filterParams && Object.keys(this.filterParams).length > 0);
    
    if (!this.initCalled && isDataReady) {
      this.initCalled = true;
      this.init();
    }
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.initCalled = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.initCalled = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseUrl = this.GENERAL_CONFIG.base_url || '';
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const grid = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = grid && Array.isArray(grid) && grid.length > 0 ? _.cloneDeep(grid) : [];
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
    };
    this.cdr.markForCheck();
  }

  init() {
    try {
      // Populate allSelectedGroups from gridData
      this.allSelectedGroups = [];
      this.gridData.forEach((row: any) => {
        this.allSelectedGroups.push(row.data);
      });

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in state group delete init:', e);
    }
  }

  deleteGroups() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build comma-separated string of group keys
    let groupKeys = '';
    this.allSelectedGroups.forEach(group => {
      groupKeys += group.MEF_GROUP_KEY + ',';
    });

    this.stateEntityService.deleteStateGroups(
      this.baseUrl,
      groupKeys,
      this.globalService,
      this.filterParams,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this._snackBar.open('Groups deleted successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit refresh event
          this.requestGridUpdate.emit('refresh');
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Error occurred in deleting the groups. If this continues please contact support', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error deleting state groups:', error);
        this._snackBar.open('Error occurred while deleting groups. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
